import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Field, Section, Select, Spinner, Textarea, type Tone } from '@zt/ui';
import type { ai } from '@zt/core';
import { api, newIdempotencyKey, type Jsonify } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { fieldError, useApiMutation } from '../../lib/mutation';
import { useMoney } from '../../lib/money';
import { usePermissions } from '../../lib/session';
import { AiButton } from './shared';

/**
 * AI Business Copilot (F9-08): hesaplanmış içgörüler (yapay zekâsız, ücretsiz) ve yalnızca bu verilerle yanıt veren
 * soru-cevap. Copilot hiçbir kaydı değiştirmez; konuşma sunucuda saklanmaz. Net kâr bölümü finans raporundan,
 * yalnızca finans raporu yetkisiyle (F9-14).
 */

const PROFIT_ROWS = [
  'revenueMinor',
  'grossProfitMinor',
  'shippingMinor',
  'feesMinor',
  'adsMinor',
  'disputesMinor',
  'expensesMinor',
  'netProfitMinor',
] as const;

type R<F extends (...a: never[]) => unknown> = Jsonify<Awaited<ReturnType<F>>>;
type Insights = R<typeof ai.copilotInsights>;
type Answer = R<typeof ai.copilotAsk>;
interface Turn {
  role: 'user' | 'assistant';
  text: string;
}

const TONE: Record<string, Tone> = { good: 'moss', info: 'slate', warn: 'amber' };

export function CopilotPage() {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ['copilot', days],
    queryFn: () => api.get<Insights>(`/v1/admin/ai/copilot/insights?days=${days}`),
  });
  const d = q.data;
  const fmtMoney = useMoney();
  const money = (minor: number) => fmtMoney(minor, d?.currency);
  const lines = (d?.insights ?? []).map((i) => t(`ai.copilot.insights.${i.key}`, i.params));
  return (
    <PageFrame helpKey="page.ai_copilot">
      <p className="max-w-[70ch] text-sm text-ink-2">{t('ai.copilot.lead')}</p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('ai.copilot.period')} className="w-44">
          {(x) => (
            <Select {...x} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[7, 30, 90].map((n) => (
                <option key={n} value={n}>
                  {t('ai.drafts.lastDays', { days: n })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {d && lines.length > 0 && (
          <Button
            onClick={() => {
              const text = [
                t('ai.copilot.reportTitle', { from: d.period.from, to: d.period.to }),
                ...lines.map((l) => `- ${l}`),
              ].join('\n');
              void navigator.clipboard?.writeText(text).then(
                () => toast.success(t('ai.drafts.copied')),
                () => undefined,
              );
            }}
          >
            <Copy className="h-4 w-4" aria-hidden />
            {t('ai.copilot.copyReport')}
          </Button>
        )}
        <HelpTip k="copilot.report" />
      </div>
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <Section title={t('ai.copilot.insightsTitle')} action={<HelpTip k="copilot.insights" />}>
            {!d.insights.length ? (
              <EmptyState title={t('ai.copilot.noInsights')} description={t('ai.copilot.noInsightsLead')} />
            ) : (
              <ul className="space-y-2">
                {d.insights.map((i, n) => (
                  <li key={`${i.key}-${n}`} className="flex items-start gap-2 text-sm">
                    <Badge tone={TONE[i.severity]}>{t(`ai.copilot.severity.${i.severity}`)}</Badge>
                    <span>{lines[n]}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-sm text-ink-3">
              {t('ai.copilot.sectionsNote', {
                hidden:
                  (['stock', 'seo', 'customers', 'ads', 'cost', 'finance'] as const)
                    .filter((k) => !d.sections[k])
                    .map((k) => t(`ai.copilot.sections.${k}`))
                    .join(', ') || '—',
              })}
            </p>
          </Section>

          {d.sales && (
            <Section title={t('ai.copilot.salesTitle', { from: d.period.from, to: d.period.to })}>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[28rem] text-sm">
                  <caption className="sr-only">{t('ai.copilot.salesTitle', d.period)}</caption>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.copilot.metric')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.copilot.current')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.copilot.previous')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {(
                      [
                        ['orders', (p: typeof d.sales.current) => formatNumber(p.orders)],
                        ['revenue', (p: typeof d.sales.current) => money(p.revenueMinor)],
                        ['refunded', (p: typeof d.sales.current) => money(p.refundedMinor)],
                        ['newCustomers', (p: typeof d.sales.current) => formatNumber(p.newCustomers)],
                      ] as const
                    ).map(([k, f]) => (
                      <tr key={k}>
                        <th scope="row" className="px-3 py-2 text-start font-normal">
                          {t(`ai.copilot.metrics.${k}`)}
                        </th>
                        <td className="px-3 py-2 text-end tabular-nums">{f(d.sales!.current)}</td>
                        <td className="px-3 py-2 text-end tabular-nums">{f(d.sales!.previous)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {d.sales.topProducts.length > 0 && (
                <>
                  <h3 className="mt-4 text-sm font-medium">{t('ai.copilot.topProducts')}</h3>
                  <ul className="mt-1 space-y-0.5 text-sm">
                    {d.sales.topProducts.map((p) => (
                      <li key={p.title}>
                        {p.title} <span className="text-ink-3">· {money(p.revenueMinor)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Section>
          )}

          {d.profit && (
            <Section
              title={t('ai.copilot.profitTitle', d.profit.period)}
              action={<HelpTip k="copilot.profit" />}
            >
              <p className="mb-3 max-w-[70ch] text-sm text-ink-2">{t('ai.copilot.profitLead')}</p>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[28rem] text-sm">
                  <caption className="sr-only">{t('ai.copilot.profitTitle', d.profit.period)}</caption>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.copilot.metric')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.copilot.current')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.copilot.previous')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {PROFIT_ROWS.map((k) => (
                      <tr key={k} className={k === 'netProfitMinor' ? 'font-medium' : undefined}>
                        <th scope="row" className="px-3 py-2 text-start font-normal">
                          {t(`ai.copilot.profitMetrics.${k}`)}
                        </th>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {fmtMoney(d.profit!.current[k], 'TRY')}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {fmtMoney(d.profit!.previous[k], 'TRY')}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <th scope="row" className="px-3 py-2 text-start font-normal">
                        {t('ai.copilot.profitMetrics.netBps')}
                      </th>
                      {[d.profit.current.netBps, d.profit.previous.netBps].map((b, n) => (
                        <td key={n} className="px-3 py-2 text-end tabular-nums">
                          {b === null ? '—' : t('ai.copilot.percent', { value: formatNumber(b / 100, 1) })}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {can('ai.generate') && <AskSection days={days} />}
        </>
      )}
    </PageFrame>
  );
}

function AskSection({ days }: { days: number }) {
  const t = useT();
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const ask = useApiMutation(
    (v: { question: string }) =>
      api.post<Answer>(
        '/v1/admin/ai/copilot/ask',
        { question: v.question, days, history: turns.slice(-6) },
        { idempotencyKey: newIdempotencyKey() },
      ),
    {
      invalidate: [['ai', 'availability']],
      silentFieldErrors: true,
      onSuccess: (r, v) => {
        setTurns((x) => [...x, { role: 'user', text: v.question }, { role: 'assistant', text: r.answer }]);
        setQuestion('');
      },
    },
  );
  return (
    <Section title={t('ai.copilot.askTitle')} action={<HelpTip k="copilot.ask" />}>
      <p className="mb-3 max-w-[70ch] text-sm text-ink-2">{t('ai.copilot.askLead')}</p>
      {turns.length > 0 && (
        <ol className="mb-4 space-y-3" aria-label={t('ai.copilot.conversation')}>
          {turns.map((x, i) => (
            <li
              key={i}
              className={`rounded-md border p-3 text-sm ${x.role === 'user' ? 'border-line bg-surface-2' : 'border-pine/30'}`}
            >
              <p className="mb-1 text-ink-3">
                {x.role === 'user' ? t('ai.copilot.you') : t('ai.copilot.copilot')}
              </p>
              <p className="whitespace-pre-line">{x.text}</p>
            </li>
          ))}
        </ol>
      )}
      <Field
        label={t('ai.copilot.question')}
        description={t('ai.copilot.questionHint')}
        error={fieldError(ask.error, 'question')}
      >
        {(x) => (
          <Textarea
            {...x}
            rows={2}
            value={question}
            maxLength={500}
            onChange={(e) => setQuestion(e.target.value)}
          />
        )}
      </Field>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <AiButton
          loading={ask.isPending}
          onClick={() => question.trim().length >= 3 && ask.mutate({ question: question.trim() })}
        >
          {t('ai.copilot.ask')}
        </AiButton>
        {turns.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setTurns([])}>
            {t('ai.copilot.clear')}
          </Button>
        )}
      </div>
    </Section>
  );
}
